import React from 'react';
import { FormGroup, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { faCheckCircle, faScrewdriver } from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    FormCheckbox, onFieldChange, onReactSelectChanged, ToastMessage, SubHeading,
    Input, NumericInput,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { EquipmentType } from './Equipment';
import { EquipmentTypePayrollIncentiveGrid } from './EquipmentTypePayrollIncentiveGrid';

export class EquipmentTypeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const initialEquipmentType = this.props.equipmentType ?? new EquipmentType();
        const stateBase = {
            equipmentType: initialEquipmentType,
            originalData: {
                ...clone(initialEquipmentType),
                payrollIncentives: initialEquipmentType.payrollIncentives ?? [], // Ensure it's an array
            },
            payrollIncentives: initialEquipmentType.payrollIncentives ?? [],
            groups: [],
            locations: [],
            show: false,
            statuses: [],
            requiredEquipment: [],
            afadTypes: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    onChange = onFieldChange.bind(this);

    onClose(response) {
        const hasUnsavedChanges =
            !isEqual(this.state.originalData, {
                ...this.state.equipmentType,
                payrollIncentives: this.state.payrollIncentives,
            });

        if (hasUnsavedChanges) {
            if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                return;
            }
        }

        this.setState({ show: false });
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onDelete = async () => {
        const { equipmentType } = this.state;
        const response = await httpClient.delete(ApiRoutes.equipmentType.delete(equipmentType.id));
        if (response) this.onClose(response);
    };

    onSelectChange = onReactSelectChanged.bind(this);

    onSubmit = async () => {
        const { equipmentType, payrollIncentives } = this.state;

        // Clear any fluent api errors
        this.setState({ errors: {}, saving: true });

        // Prepare the payload for submission
        const payload = {
            ...equipmentType,
            payrollIncentives: payrollIncentives.map((incentive) => ({
                ...incentive,
                id: typeof incentive.id === 'string' ? null : incentive.id || null,
            })),
        };

        // Is this POST or PUT?
        const url = equipmentType.id
            ? ApiRoutes.equipmentType.update(equipmentType.id)
            : ApiRoutes.equipmentType.create();
        const fetchMethod = equipmentType.id ? httpClient.put : httpClient.post;

        try {
            // Send the payload to the API
            const response = await fetchMethod(url, payload);

            if (response) {
                // Show success notification
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message={`Equipment Type [${equipmentType.description}] saved.`}
                    />,
                );

                // Reset the form state and close the form
                this.setState({ show: false, saving: false });
                this.context.setFormOpened(false);
                this.resetForm();

                // Notify the parent component of successful save
                this.props.onSaveCallback(response);
            }
        } catch (err) {
            // Handle errors (e.g., validation or server errors)
            this.handleSaveError(err);
        } finally {
            // Reset saving state
            this.setState({ saving: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);


    getSelectedAFADType = () => {
        const { afadTypes, equipmentType } = this.state;

        return (afadTypes ?? []).find(
            (s) => s.value === equipmentType.equipmentTypeAFADTypeId,
        ) ?? 1;
    };

    open = async (id) => {
        this.resetForm();
        this.setState({ show: true, loading: true });
        this.context.setFormOpened(true);

        let [equipmentType, requiredEquipmentOptions] = await Promise.all([
            (id ? httpClient.js(ApiRoutes.equipmentType.byId(id)) : new EquipmentType()),
            httpClient.js(ApiRoutes.equipmentType.all()),
        ]);

        await this.populateState();

        if (equipmentType.id) {
            // Cannot assign dependent type as itself
            requiredEquipmentOptions = (requiredEquipmentOptions ?? [])
                .filter((x) => x.id !== equipmentType.id);
        }

        requiredEquipmentOptions = requiredEquipmentOptions.map((x) => ({ value: x.id, label: x.description }));

        // Fetch payroll incentives for this equipment type
        const payrollIncentives = equipmentType.payrollIncentives.map((incentive) => ({
            id: incentive.id ?? null,
            amount: incentive.amount ?? 0,
            applicationUserGroupId: incentive.applicationUserGroupId ?? null,
            usStateId: incentive.usStateId ?? null,
            certificationId: incentive.certificationId ?? null,
            editing: false, // Set editing to false initially
        }));

        this.setState({
            equipmentType,
            originalData: {
                ...clone(equipmentType),
                payrollIncentives,
            },
            requiredEquipment: requiredEquipmentOptions,
            payrollIncentives,
            loading: false,
        });
    };

    async populateState() {
        const [groups, afadTypes, certifications, assetTypes] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.employeeGroups()),
            httpClient.js(ApiRoutes.typeAheads.equipmentTypeAFADTypes()),
            httpClient.js(ApiRoutes.certification.all()),
            httpClient.js(ApiRoutes.assetManagement.assetTypes()),
        ]);

        let usStates = [];

        if (this.context.tenant.tenantSettings.tenantCountryId) {
            usStates = await httpClient.js(
                ApiRoutes.typeAheads.USStatesbyCountryId(this.context.tenant.tenantSettings.tenantCountryId),
            );
        }

        this.setState({
            groups,
            afadTypes,
            usStates,
            certifications,
            assetTypes,
        });
    }

    resetForm() {
        this.setState({ formValidated: false });
    }

    handleIncentivesChange = (updatedIncentives) => {
        this.setState({ payrollIncentives: updatedIncentives });
    };

    render() {
        const {
            requiredEquipment, groups, formValidated, validationMessage,
            afadTypes, originalData, equipmentType, payrollIncentives,
            assetTypes,
        } = this.state;

        const selectedAFADType = this.getSelectedAFADType();

        const hasUnsavedChanges = !isEqual(originalData, {
            ...equipmentType,
            payrollIncentives,
        });

        if (!this.context.tenant.tenantSettings) {
            return null;
        }


        return (
            <>
                <FormPrompt
                    when={hasUnsavedChanges}
                    message="You have unsaved changes. Are you sure you want to leave?"
                />
                <SlideForm
                    loading={this.state.loading}
                    show={this.state.show}
                    id="equipmentTypeForm"
                    formIcon={faScrewdriver}
                    formTitle={!!parseInt(equipmentType.id ?? 0) > 0 ? 'Edit Equipment Type' : 'Add Equipment Type'}
                    ref={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    onSave={this.onSave}
                    onDelete={this.onDelete}
                    errors={this.state.errors}
                    onClearErrors={this.onClearErrors}
                    error={validationMessage}
                    size="col-xl-5"
                >
                    <Input
                        label="Description"
                        id="description"
                        name="equipmentType.description"
                        value={equipmentType.description ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter description"
                        error="Description is required."
                        required
                    />
                    <ValidatedSelect
                        id="groups"
                        name="equipmentType.groups"
                        isMulti
                        label="Required User Groups"
                        helpMessage="Select any user groups that are required for this type of equipment."
                        options={groups}
                        value={(groups ?? []).filter((x) => (equipmentType.groups ?? []).includes(x.value)) ?? ''}
                        onChange={this.onSelectChange}
                        error="At least one is required: required user group or required equipment."
                        required={!(equipmentType.requiredEquipment ?? []).length}
                    />

                    <NumericInput
                        helpMessage="Enter the amount that can be assigned to any of the selected user groups."
                        label="Employee Assignable Quantity"
                        id="employeeAssignableQuantity"
                        name="equipmentType.employeeAssignableQuantity"
                        disabled={!(equipmentType.groups ?? []).length}
                        value={equipmentType.employeeAssignableQuantity ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter Quantity"
                        step="1"
                        required={!!(equipmentType.groups ?? []).length}
                        error="Employee Assignable Quantity is required."
                    />

                    <ValidatedSelect
                        id="requiredEquipment"
                        name="equipmentType.requiredEquipment"
                        label="Required Equipment"
                        isMulti
                        options={requiredEquipment}
                        value={(requiredEquipment ?? []).filter((x) => (equipmentType.requiredEquipment ?? []).includes(x.value)) ?? ''}
                        onChange={this.onSelectChange}
                        required={!(equipmentType.groups ?? []).length}
                        helpMessage="Select other equipment types that will be required in addition to this equipment when it is assigned."
                        error="At least one is required: required user group or required equipment."
                    />
                    <NumericInput
                        label="Equipment Assignable Quantity"
                        helpMessage="Enter the amount that can be assigned to any of the selected equipment."
                        id="equipmentAssignableQuantity"
                        name="equipmentType.equipmentAssignableQuantity"
                        disabled={!(equipmentType.requiredEquipment ?? []).length}
                        value={equipmentType.equipmentAssignableQuantity ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter Quantity"
                        step="1"
                        required={!!(equipmentType.requiredEquipment ?? []).length}
                        error="Equipment Assignable Quantity is required."
                    />

                    <ValidatedSelect
                        id="equipmentTypeAFADTypeId"
                        name="equipmentType.equipmentTypeAFADTypeId"
                        options={afadTypes}
                        label="Is this a type of AFAD?"
                        required
                        value={selectedAFADType}
                        onChange={this.onSelectChange}
                        error="You must choose what type of equipment this is."
                    />

                    {!selectedAFADType.isAFAD && (
                        <FormGroup>
                            <FormCheckbox
                                className="mt-1"
                                id="isVehicle"
                                name="equipmentType.isVehicle"
                                onChange={this.onChange}
                                checked={equipmentType.isVehicle}
                                labelText="This is a vehicle"
                            />
                        </FormGroup>
                    )}

                    <ValidatedSelect
                        id="isGeneric"
                        name="equipmentType.isGeneric"
                        label="Equipment Type Usage"
                        isClearable={false}
                        options={[
                            { value: false, label: 'Specific' },
                            { value: true, label: 'Generic' },
                        ]}
                        required
                        error="Please select usage type."
                        value={
                            equipmentType.isGeneric
                                ? { value: true, label: 'Generic' }
                                : { value: false, label: 'Specific' }
                        }
                        onChange={(option) => {
                            this.setState((prevState) => ({
                                equipmentType: {
                                    ...prevState.equipmentType,
                                    isGeneric: option.value,
                                },
                            }));
                        }}
                    />
                    {!equipmentType.isGeneric && (
                        <>
                            <ValidatedSelect
                                id="assetTypeExternalId"
                                name="equipmentType.assetTypeExternalId"
                                label="NetSuite Asset Type"
                                isClearable={false}
                                options={(assetTypes || [])
                                    .filter((type) => type.isInactive == 'F' || equipmentType.assetTypeExternalId === type.value)}
                                value={
                                    (assetTypes ?? []).find(
                                        (s) => s.value === equipmentType.assetTypeExternalId,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChange}
                            />
                            <FormGroup>
                                <FormCheckbox
                                    className="mt-1"
                                    id="IsSyncEnabled"
                                    name="equipmentType.isSyncEnabled"
                                    onChange={this.onChange}
                                    checked={equipmentType.isSyncEnabled}
                                    labelText="NetSuite Sync Enabled"
                                />
                            </FormGroup>
                        </>
                    )}
                    <Row className="mt-3">
                        <Col>
                            <SubHeading>Pay Incentives</SubHeading>
                            <EquipmentTypePayrollIncentiveGrid
                                incentives={this.state.payrollIncentives}
                                onIncentivesChange={this.handleIncentivesChange}
                                certifications={(this.state.certifications ?? []).map((x) => ({ value: x.id, label: x.name }))}
                                usStates={this.state.usStates}
                                userGroups={this.state.groups.filter((group) =>
                                    (this.state.equipmentType.groups ?? []).includes(group.value),
                                )}
                            />

                        </Col>
                    </Row>
                </SlideForm>
            </>
        );
    }
}
